import {
	Apple,
	Check,
	FileText,
	Smartphone,
	Upload,
	Users,
} from "lucide-react";
import { AnimatePresence, motion, type Variants } from "motion/react";
import type React from "react";
import { BrowserFrame } from "@/components/blocks/frames/Frames";
import { EASE, SPRING } from "@/components/motion/motion";
import { useSceneLoop } from "@/hooks/use-scene-loop";
import { useI18n } from "@/lib/i18n/use-i18n";
import { cn } from "@/lib/utils";

const STAGES = ["diagnostic", "plan", "build", "handover"] as const;
const PIPELINE_DURATIONS = [3400, 3000, 3800, 3400] as const;

/*
 * Stage content animates through variants inherited from the keyed panel, so
 * `AnimatePresence initial={false}` server-renders the finished state.
 */
const panel: Variants = {
	hidden: { opacity: 0, y: 10 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.35, ease: EASE } },
	exit: { opacity: 0, y: -8, transition: { duration: 0.25, ease: EASE } },
};
const item: Variants = {
	hidden: { opacity: 0, x: -8 },
	visible: (i = 0) => ({
		opacity: 1,
		x: 0,
		transition: { duration: 0.4, ease: EASE, delay: 0.1 + i * 0.12 },
	}),
};
const bar: Variants = {
	hidden: { scaleX: 0 },
	visible: (i = 0) => ({
		scaleX: 1,
		transition: { duration: 0.7, ease: EASE, delay: 0.2 + i * 0.55 },
	}),
};
const pop: Variants = {
	hidden: { scale: 0 },
	visible: (i = 0) => ({
		scale: 1,
		transition: { ...SPRING, delay: 0.85 + i * 0.55 },
	}),
};
const rail: Variants = {
	hidden: { scaleY: 0 },
	visible: { scaleY: 1, transition: { duration: 1.2, ease: EASE, delay: 0.2 } },
};

/**
 * The four stages of an Expo migration in one window: native dependencies
 * sorted, plan ordered, builds verified, workflow handed over.
 * Controlled by `stage`, or cycling on its own (server renders the builds).
 */
export function PipelineScene({
	stage,
	className,
}: {
	stage?: number;
	className?: string;
}) {
	const { dict } = useI18n();
	const pipeline = dict.scenes.pipeline;
	const loop = useSceneLoop(PIPELINE_DURATIONS, {
		initial: 2,
		enabled: stage === undefined,
	});
	const current = stage ?? loop.index;

	const body: Record<(typeof STAGES)[number], React.ReactNode> = {
		diagnostic: (
			<Panel title={pipeline.deps.title}>
				<ul className="space-y-2">
					{(["a", "b", "c", "d"] as const).map((key, row) => (
						<motion.li
							key={key}
							variants={item}
							custom={row}
							className="flex items-center gap-3 rounded-lg border bg-background px-3 py-2"
						>
							<span className="min-w-0 flex-1 truncate font-mono text-[11px]">
								{pipeline.deps[key].name}
							</span>
							<span
								className={cn(
									"shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium",
									key === "c"
										? "bg-signal text-signal-foreground"
										: "bg-foreground/[0.06]",
								)}
							>
								{pipeline.deps[key].verdict}
							</span>
						</motion.li>
					))}
				</ul>
			</Panel>
		),
		plan: (
			<Panel title={pipeline.plan.title}>
				<ol className="relative space-y-2 pl-7">
					<motion.span
						className="absolute top-2 bottom-2 left-[0.6rem] w-px origin-top bg-signal"
						variants={rail}
					/>
					{(["a", "b", "c", "d"] as const).map((key, row) => (
						<motion.li
							key={key}
							variants={item}
							custom={row}
							className="relative flex items-center gap-3 py-1 text-[12px] font-medium"
						>
							<span className="absolute -left-7 flex size-5 items-center justify-center rounded-full border bg-background font-mono text-[9px]">
								{row + 1}
							</span>
							{pipeline.plan[key]}
						</motion.li>
					))}
				</ol>
			</Panel>
		),
		build: (
			<Panel title={pipeline.build.title}>
				<ul className="space-y-2.5">
					{(
						[
							["ios", Apple],
							["android", Smartphone],
							["submit", Upload],
							["flows", Check],
						] as const
					).map(([key, Icon], row) => (
						<li key={key} className="flex items-center gap-3 text-[11.5px]">
							<Icon className="size-3.5 shrink-0 text-muted-foreground" />
							<span className="w-[42%] shrink-0 truncate font-medium">
								{pipeline.build[key]}
							</span>
							<span className="h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/[0.07]">
								<motion.span
									className="block h-full origin-left rounded-full bg-signal"
									variants={bar}
									custom={row}
								/>
							</span>
							<motion.span
								className="flex size-4 shrink-0 items-center justify-center rounded-full bg-signal text-signal-foreground"
								variants={pop}
								custom={row}
							>
								<Check className="size-2.5" strokeWidth={3} />
							</motion.span>
						</li>
					))}
				</ul>
			</Panel>
		),
		handover: (
			<Panel title={pipeline.handover.title}>
				<div className="grid grid-cols-[1fr_1.2fr] gap-3">
					<motion.div
						variants={item}
						custom={0}
						className="relative rounded-lg border bg-background p-3"
					>
						<FileText className="mb-2 size-4 text-signal-ink" />
						<div className="space-y-1.5">
							{["w-full", "w-4/5", "w-full", "w-3/5"].map((width, line) => (
								<span
									key={`${width}-${line}`}
									className={cn(
										"block h-1.5 rounded-full bg-foreground/[0.08]",
										width,
									)}
								/>
							))}
						</div>
					</motion.div>
					<ul className="space-y-2 text-[11px] font-medium">
						{(
							[
								["doc", FileText],
								["session", Users],
								["owner", Upload],
							] as const
						).map(([key, Icon], row) => (
							<motion.li
								key={key}
								variants={item}
								custom={row + 1}
								className={cn(
									"flex items-center gap-2 rounded-lg border px-2.5 py-2",
									key === "owner" && "border-signal/50 bg-signal/10",
								)}
							>
								<Icon className="size-3.5 shrink-0" />
								<span className="leading-tight">{pipeline.handover[key]}</span>
							</motion.li>
						))}
					</ul>
				</div>
			</Panel>
		),
	};

	return (
		<div ref={loop.ref} className={cn("w-full", className)}>
			<BrowserFrame host={pipeline.file} tag={dict.common.illustrationBadge}>
				<div className="border-b px-4 pt-3">
					<ol className="grid grid-cols-4 gap-2">
						{STAGES.map((key, stageIndex) => (
							<li
								key={key}
								className={cn(
									"truncate pb-2.5 text-[10.5px] font-medium transition-colors duration-300",
									stageIndex === current
										? "text-foreground"
										: stageIndex < current
											? "text-muted-foreground"
											: "text-muted-foreground/70",
								)}
							>
								<span className="mr-1.5 font-mono text-[9px] text-signal-ink">
									0{stageIndex + 1}
								</span>
								{pipeline.stages[key]}
							</li>
						))}
					</ol>
					<div className="relative -mx-4 h-0.5 bg-foreground/[0.06]">
						<motion.span
							className="absolute inset-y-0 left-0 w-full origin-left bg-signal"
							initial={false}
							animate={{ scaleX: (current + 1) / STAGES.length }}
							transition={SPRING}
						/>
					</div>
				</div>
				<div className="min-h-[13.5rem] p-4">
					<AnimatePresence mode="wait" initial={false}>
						<motion.div
							key={current}
							variants={panel}
							initial="hidden"
							animate="visible"
							exit="exit"
						>
							{body[STAGES[current] ?? "build"]}
						</motion.div>
					</AnimatePresence>
				</div>
			</BrowserFrame>
		</div>
	);
}

function Panel({
	title,
	children,
}: {
	title: string;
	children: React.ReactNode;
}) {
	return (
		<div>
			<p className="mb-3 text-xs font-semibold">{title}</p>
			{children}
		</div>
	);
}
